import {
  serializeReportFilterUrl,
  videoLabel,
  type ExactVideoDetailResponse,
  type ExactVideoTrendResponse,
  type ReportFilterStateV1,
  type ReportMetricId,
  type VideoSelection,
} from "@meta-video/contracts";
import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";

import {
  AccessDeniedError,
  SessionExpiredError,
} from "../../shared/api/client.js";
import { ShareLinkButton } from "../workspace/ShareLinkButton.js";
import { exactVideoQueryKey, type ReportApi } from "../report/api.js";
import { VideoAdsPanel } from "../report/VideoAdsPanel.js";
import { VideoIdentityCell } from "../report/VideoIdentityCell.js";
import { reportingEnvelopeFingerprint } from "../report/reportingEnvelope.js";
import { TrendChart } from "../trend/TrendChart.js";
import { StatusBadge } from "./StatusBadge.js";
import type { VideoApi } from "./api.js";
import { VideoMetricSummary } from "./VideoMetricSummary.js";
import { VideoPlayer } from "./VideoPlayer.js";

interface ExactSnapshot {
  readonly detail: ExactVideoDetailResponse;
  readonly filterKey: string;
  readonly identityKey: string;
  readonly requestFilters: ReportFilterStateV1;
  readonly spend: ExactVideoTrendResponse;
}

const DETAIL_AD_COLUMNS = [
  "spend",
  "revenue",
  "roas",
  "ctr",
  "hookRate",
  "holdRate",
] as const satisfies readonly ReportMetricId[];

export interface VideoDetailViewProps {
  readonly api: ReportApi;
  readonly contextIssue?: ReactNode;
  readonly contextUpdating?: boolean;
  readonly expectedFingerprint: string;
  readonly filters: ReportFilterStateV1;
  readonly onAccessChanged?: () => void;
  readonly onAddToComparison: (selection: VideoSelection) => void;
  readonly onBack: () => void;
  readonly onSessionExpired?: () => void;
  readonly playbackApi?: VideoApi;
  readonly selection: VideoSelection;
}

export function VideoDetailHeader({
  children,
  label,
  onBack,
}: {
  readonly children?: ReactNode;
  readonly label: string;
  readonly onBack: () => void;
}) {
  return (
    <Group className="video-detail__actions" justify="space-between" wrap="wrap">
      <Group gap="sm" wrap="wrap">
        <Button autoFocus onClick={onBack} size="compact-sm" variant="default">
          Back to report
        </Button>
        <h1>{label}</h1>
      </Group>
      {children === undefined ? (
        <ShareLinkButton analyticsSurface="detail" compactLabel="Share" label="Share video detail" />
      ) : (
        <Group gap="xs" wrap="wrap">
          <ShareLinkButton analyticsSurface="detail" compactLabel="Share" label="Share video detail" />
          {children}
        </Group>
      )}
    </Group>
  );
}

export function VideoDetailView({
  api,
  contextIssue,
  contextUpdating = false,
  expectedFingerprint,
  filters,
  onAccessChanged,
  onAddToComparison,
  onBack,
  onSessionExpired,
  playbackApi,
  selection,
}: VideoDetailViewProps) {
  const filterKey = serializeReportFilterUrl(filters);
  const identityKey = `${selection.brandId}\0${selection.videoId}`;
  const lastCoherent = useRef<ExactSnapshot | null>(null);
  const detail = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => api.getVideoDetail({ filters, selection }, { signal }),
    queryKey: [
      ...exactVideoQueryKey("detail", filterKey, selection, api),
      expectedFingerprint,
    ],
    refetchOnWindowFocus: false,
  });
  const spend = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => api.getVideoSpendTrend({ filters, selection }, { signal }),
    queryKey: [
      ...exactVideoQueryKey("spend", filterKey, selection, api),
      expectedFingerprint,
    ],
    refetchOnWindowFocus: false,
  });
  const updating = contextUpdating || detail.isFetching || spend.isFetching;
  const accessDenied = detail.error instanceof AccessDeniedError
    || spend.error instanceof AccessDeniedError;
  const queryFingerprintsMatch = detail.data !== undefined
    && spend.data !== undefined
    && reportingEnvelopeFingerprint(detail.data)
      === reportingEnvelopeFingerprint(spend.data);
  const matchesReport = queryFingerprintsMatch
    && reportingEnvelopeFingerprint(detail.data!) === expectedFingerprint;
  const currentSnapshot: ExactSnapshot | null = matchesReport
    && !detail.isPlaceholderData
    && !spend.isPlaceholderData
    && !updating
    && detail.error === null
    && spend.error === null
    ? {
        detail: detail.data!,
        filterKey,
        identityKey,
        requestFilters: filters,
        spend: spend.data!,
      }
    : null;
  const previous = lastCoherent.current;
  const snapshot = accessDenied
    ? null
    : currentSnapshot ?? (
        previous?.identityKey === identityKey
          ? previous
          : null
      );
  const disabled = currentSnapshot === null && snapshot !== null;
  const contextUnavailable = contextIssue !== null && contextIssue !== undefined;
  const outOfSync = !accessDenied
    && detail.data !== undefined
    && spend.data !== undefined
    && !updating
    && !matchesReport;
  const currentDetail = !accessDenied
    && detail.data !== undefined
    && !detail.isPlaceholderData
    && reportingEnvelopeFingerprint(detail.data) === expectedFingerprint
    ? detail.data
    : null;
  const currentSpend = !accessDenied
    && spend.data !== undefined
    && !spend.isPlaceholderData
    && reportingEnvelopeFingerprint(spend.data) === expectedFingerprint
    ? spend.data
    : null;

  useEffect(() => {
    if (
      detail.error instanceof SessionExpiredError
      || spend.error instanceof SessionExpiredError
    ) onSessionExpired?.();
  }, [detail.error, onSessionExpired, spend.error]);

  useLayoutEffect(() => {
    if (!accessDenied) return;
    lastCoherent.current = null;
    onAccessChanged?.();
  }, [accessDenied, onAccessChanged]);

  const hasExactReadError = (
    detail.error !== null
    && !(detail.error instanceof SessionExpiredError)
    && !(detail.error instanceof AccessDeniedError)
  ) || (
    spend.error !== null
    && !(spend.error instanceof SessionExpiredError)
    && !(spend.error instanceof AccessDeniedError)
  );
  const row = snapshot?.detail.row ?? null;
  const headingRow = row ?? (hasExactReadError ? currentDetail?.row : null) ?? null;
  const label = headingRow === null ? selection.videoId : videoLabel(headingRow).primary;
  const exactFingerprint = snapshot === null
    ? expectedFingerprint
    : reportingEnvelopeFingerprint(snapshot.detail);

  useEffect(() => {
    lastCoherent.current = null;
  }, [identityKey]);

  useEffect(() => {
    if (currentSnapshot !== null) lastCoherent.current = currentSnapshot;
  }, [currentSnapshot]);

  return (
    <section
      aria-busy={updating && !contextUnavailable}
      aria-label="Video detail"
      className={`video-detail${disabled ? " video-detail--updating" : ""}`}
      data-video-key={identityKey}
      role="region"
    >
      <VideoDetailHeader label={label} onBack={onBack}>
        <Button
          disabled={disabled || row === null}
          onClick={() => onAddToComparison(selection)}
          size="compact-sm"
          variant="light"
        >
          Add to comparison
        </Button>
      </VideoDetailHeader>
      {contextIssue}
      {accessDenied ? (
        <Text aria-label="Report access changed" role="status" size="sm">
          Report access changed. Rechecking your permitted workspace…
        </Text>
      ) : null}
      {detail.isPending || spend.isPending ? (
        <div aria-live="polite" className="report-loading" role="status">
          <Loader size="sm" /> Loading video detail…
        </div>
      ) : null}
      {disabled && !contextUnavailable ? (
        <div aria-label="Updating video detail" aria-live="polite" className="report-updating" role="status">
          <Loader size="xs" /> Updating video detail… previous results remain visible.
        </div>
      ) : null}
      {hasExactReadError ? (
        <Stack className="video-detail__partial" gap="md">
          {detail.error && !(detail.error instanceof SessionExpiredError) ? (
            <ExactReadError
              description="The exact KPI summary and playback status could not be loaded. Accurate spend history remains available below."
              onRetry={() => void detail.refetch()}
              retryLabel="Retry video summary"
              title="Video summary could not be loaded"
            />
          ) : null}
          {spend.error && !(spend.error instanceof SessionExpiredError) ? (
            <ExactReadError
              description="Daily spend could not be loaded. Accurate summary and contributing ads remain available below."
              onRetry={() => void spend.refetch()}
              retryLabel="Retry daily spend"
              title="Daily spend could not be loaded"
            />
          ) : null}
          {snapshot === null && currentDetail?.row ? (
            <PartialDetailSummary
              api={api}
              detail={currentDetail}
              filterKey={filterKey}
              filters={filters}
              onAccessChanged={onAccessChanged}
              onSessionExpired={onSessionExpired}
              playbackApi={playbackApi}
              row={currentDetail.row}
              selection={selection}
            />
          ) : snapshot === null && currentDetail?.row === null ? (
            <Alert color="blue" role="status" title="Video not found in this report context">
              This authorized identity has no matching summary under the current filters.
            </Alert>
          ) : null}
          {snapshot === null && currentSpend ? (
            <TrendChart defaultExpanded metric="spend" trend={currentSpend} />
          ) : null}
        </Stack>
      ) : null}
      {!hasExactReadError && outOfSync ? (
        <Alert color="red" role="alert" title="Video detail is out of sync">
          <Stack gap="xs">
            <Text size="sm">
              Summary, spend history, and the report resolved against different snapshots, so they are not combined.
            </Text>
            <Button onClick={() => void Promise.all([detail.refetch(), spend.refetch()])} size="compact-sm" variant="light">
              Reload video detail
            </Button>
          </Stack>
        </Alert>
      ) : null}
      {snapshot && row === null ? (
        <Alert color="blue" role="status" title="Video not found in this report context">
          This authorized identity has no matching metrics under the current filters.
        </Alert>
      ) : null}
      {snapshot && row ? (
        <div
          aria-disabled={disabled ? "true" : undefined}
          className="video-detail__layout"
          inert={disabled ? true : undefined}
        >
          <aside className="video-detail__identity">
            <Stack gap="md">
              <VideoIdentityCell identity={row} />
              <StatusBadge status={row.playStatus} />
              {row.playStatus === "hosted" ? (
                <VideoPlayer
                  api={playbackApi}
                  key={identityKey}
                  label={label}
                  onAccessChanged={onAccessChanged}
                  onSessionExpired={onSessionExpired}
                  selection={selection}
                  surface="detail"
                />
              ) : null}
              <DetailContext detail={snapshot.detail} filterKey={snapshot.filterKey} />
            </Stack>
          </aside>
          <Stack className="video-detail__analytics" gap="md">
            {snapshot.detail.cacheStatus === "stale"
            || snapshot.spend.cacheStatus === "stale" ? (
              <Alert color="orange" role="alert" title="Cached video analytics may be out of date">
                Freshness checks are unavailable; this complete exact-video snapshot may be out of date.
              </Alert>
            ) : null}
            <VideoMetricSummary
              row={row}
              videoMetricCoverageThreshold={snapshot.detail.coverage.videoMetricThreshold}
            />
            <TrendChart defaultExpanded metric="spend" trend={snapshot.spend} />
            <div aria-labelledby="video-detail-ads-heading">
              <h2 id="video-detail-ads-heading">Contributing ads</h2>
              <VideoAdsPanel
                api={api}
                columnIds={DETAIL_AD_COLUMNS}
                expectedFingerprint={exactFingerprint}
                filters={snapshot.requestFilters}
                onAccessChanged={onAccessChanged}
                onSessionExpired={onSessionExpired}
                selection={selection}
                videoMetricCoverageThreshold={snapshot.detail.coverage.videoMetricThreshold}
              />
            </div>
          </Stack>
        </div>
      ) : null}
    </section>
  );
}

function ExactReadError({
  description,
  onRetry,
  retryLabel,
  title,
}: {
  readonly description: string;
  readonly onRetry: () => void;
  readonly retryLabel: string;
  readonly title: string;
}) {
  return (
    <Alert color="red" role="alert" title={title}>
      <Stack gap="xs">
        <Text size="sm">{description}</Text>
        <Button onClick={onRetry} size="compact-sm" variant="light">
          {retryLabel}
        </Button>
      </Stack>
    </Alert>
  );
}

function PartialDetailSummary({
  api,
  detail,
  filterKey,
  filters,
  onAccessChanged,
  onSessionExpired,
  playbackApi,
  row,
  selection,
}: {
  readonly api: ReportApi;
  readonly detail: ExactVideoDetailResponse;
  readonly filterKey: string;
  readonly filters: ReportFilterStateV1;
  readonly onAccessChanged?: () => void;
  readonly onSessionExpired?: () => void;
  readonly playbackApi?: VideoApi;
  readonly row: NonNullable<ExactVideoDetailResponse["row"]>;
  readonly selection: VideoSelection;
}) {
  const identityKey = `${selection.brandId}\0${selection.videoId}`;
  const label = videoLabel(row).primary;
  return (
    <div className="video-detail__layout">
      <aside className="video-detail__identity">
        <Stack gap="md">
          <VideoIdentityCell identity={row} />
          <StatusBadge status={row.playStatus} />
          {row.playStatus === "hosted" ? (
            <VideoPlayer
              api={playbackApi}
              key={identityKey}
              label={label}
              onAccessChanged={onAccessChanged}
              onSessionExpired={onSessionExpired}
              selection={selection}
              surface="detail"
            />
          ) : null}
          <DetailContext detail={detail} filterKey={filterKey} />
        </Stack>
      </aside>
      <Stack className="video-detail__analytics" gap="md">
        <VideoMetricSummary
          row={row}
          videoMetricCoverageThreshold={detail.coverage.videoMetricThreshold}
        />
        <div aria-labelledby="video-detail-partial-ads-heading">
          <h2 id="video-detail-partial-ads-heading">Contributing ads</h2>
          <VideoAdsPanel
            api={api}
            columnIds={DETAIL_AD_COLUMNS}
            expectedFingerprint={reportingEnvelopeFingerprint(detail)}
            filters={filters}
            onAccessChanged={onAccessChanged}
            onSessionExpired={onSessionExpired}
            selection={selection}
            videoMetricCoverageThreshold={detail.coverage.videoMetricThreshold}
          />
        </div>
      </Stack>
    </div>
  );
}

function DetailContext({
  detail,
  filterKey,
}: {
  readonly detail: ExactVideoDetailResponse;
  readonly filterKey: string;
}) {
  const lastSync = detail.freshness.lastSyncAt === null
    ? "Unavailable"
    : new Date(detail.freshness.lastSyncAt).toLocaleString("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "UTC",
      });
  return (
    <dl aria-label="Video detail context" className="video-detail__context">
      <div><dt>Date range</dt><dd>{detail.effectiveRange.from} – {detail.effectiveRange.to}</dd></div>
      <div><dt>Complete through</dt><dd>{detail.completeness.latestCompleteDay ?? "Unavailable"}</dd></div>
      <div><dt>Freshness</dt><dd>{lastSync}{lastSync === "Unavailable" ? "" : " UTC"}</dd></div>
      <div><dt>Maturity cutoff</dt><dd>{detail.maturityCutoff}</dd></div>
      <div><dt>Video coverage</dt><dd>{detail.row?.videoCoverageRate === null || detail.row === null ? "No data" : `${Math.round(detail.row.videoCoverageRate * 100)}%`}</dd></div>
      <div><dt>Cache status</dt><dd>{detail.cacheStatus === "stale" ? "Cached fallback" : "Current snapshot"}</dd></div>
      <div><dt>Filter context</dt><dd title={filterKey}>Current report filters</dd></div>
    </dl>
  );
}
